import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import * as FileSaver from "file-saver";
import * as ExcelJs from "exceljs";
import { logDownloadSchedule } from "utils/builder/IAmecLog";
import { viewerGetScreenShot } from "../../utils/builder/IScheduleLogic";
import "./DownloadInfoTableExcel.scss";
import $ from "jquery";
import { ColorConditionService } from "utils/services/colorCondition.service";
import UniversalButton from "components/uiComponents/UniversalButton";
import { ReactComponent as ReportIcon } from "static/icons/report-download.svg";
import { useDashboardState } from "components/DashboardContext";
import { useSelector } from "react-redux";
import { getTabs } from "store/selectors/tabsSettings";

const FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const FILE_EXTENSION = ".xlsx";

function computeColorForCell({ columnId, cellValue, rowData, tableData, tabSetting }) {
  const defaultColor = null;
  const { colorScheme, uniqueColorsData } = tabSetting?.[columnId] ?? { colorScheme: null, uniqueColorsData: null };
  if (!colorScheme) {
    return defaultColor;
  }
  const colorData = ColorConditionService.getColorByColorScheme({
    colorScheme,
    uniqueColorsData,
    tableData,
    currentValue: cellValue,
  });
  return colorData?.color ?? defaultColor;
}

const DownloadInfoTableExcel = ({
  columns,
  filteredData,
  selectedData,
  fileName,
  isChecked,
}) => {
  const { t } = useTranslation();
  const { getColumnHeaderLabel } = useDashboardState();
  const tabSetting = useSelector(getTabs)?.[1] ?? null;

  const safeColumns = Array.isArray(columns) ? columns : [];

  const dataToExport = useMemo(() => {
    const source = isChecked ? selectedData : filteredData;
    return Array.isArray(source) ? source.filter(Boolean) : [];
  }, [selectedData, filteredData, isChecked]);

  const cellColors = useMemo(() => {
    if (!dataToExport.length) return [];
    return dataToExport.map(row => {
      if (!row || typeof row !== "object") return safeColumns.map(() => null);
      return safeColumns.map(column => {
        return computeColorForCell({
          columnId: column.id,
          cellValue: row[column.id],
          tabSetting,
        });
      });
    });
  }, [columns, dataToExport, tabSetting]);

  const exportToCSV = () => {
    const workbook = new ExcelJs.Workbook();
    const sheet = workbook.addWorksheet("data");
    const elementColumn = safeColumns.findIndex(item => item.id === "BIMSearch_element_header");

    sheet.columns = safeColumns.map((column) => {
      const header = getColumnHeaderLabel(column);
      return {
        header,
        key: column.accessorKey,
      };
    });

    dataToExport.forEach((line, i) => {
      if (!line || typeof line !== "object") return;
      const values = safeColumns.reduce((obj, item) => {
        obj[item.id] = line?.[item.id];
        return obj;
      }, {});

      const rowInSheet = sheet.addRow(Object.values(values));

      safeColumns.forEach((column, columnIndex) => {
        const cell = rowInSheet.getCell(columnIndex + 1);

        let colorArgb = 'FF000000';
        if (cellColors[i] && cellColors[i][columnIndex]) {
          const cellColor = cellColors[i][columnIndex];
          if (cellColor.charAt(0) === '#') {
            colorArgb = `FF${cellColor.slice(1).toUpperCase()}`;
          }
        }

        cell.font = {
          size: 12,
          color: { argb: colorArgb }
        };
      });
    });

    const { rowCount, columnCount } = sheet;
    const rowCountFinal = rowCount - 1;
    sheet.addRow([""]);

    if (rowCount > 0) {
      var cellRef = "A" + (rowCountFinal + 3);
      var cellValue = {
        formula: "COUNTA(A" + 2 + ":A" + (rowCountFinal + 1) + ")",
      };
      sheet.getCell(cellRef).value = cellValue;

      for (var i = 1; i <= columnCount; i++) {
        cellRef = letterFromNumber(i) + (rowCountFinal + 3);
        cellValue = {
          formula:
            "SUM(" +
            letterFromNumber(i) +
            2 +
            ":" +
            letterFromNumber(i) +
            (rowCountFinal + 1) +
            ")",
        };
        if (i === elementColumn + 1) {
          sheet.getCell(cellRef).value = "Total: " + rowCountFinal;
        } else {
          sheet.getCell(cellRef).value = cellValue;
        }
      }
    }

    const colorFillHeader = "FFF29B1A";
    const colorFillTotals = "FF95B3D7";
    const commonPatternFill = "solid";
    const colorBorder = "FF181818";
    const borderStyle = "thin";
    const alignmentCenter = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    const borderThinBlack = {
      top: { style: borderStyle, color: { argb: colorBorder } },
      insetInlineEnd: { style: borderStyle, color: { argb: colorBorder } },
      bottom: { style: borderStyle, color: { argb: colorBorder } },
      insetInlineStart: { style: borderStyle, color: { argb: colorBorder } },
    };
    const headerRowIndex = 1;
    const totalsRowIndex = sheet.rowCount;

    for (var rowIndex = 1; rowIndex <= sheet.rowCount; rowIndex++) {
      for (var columnIndex = 1; columnIndex <= sheet.columnCount; columnIndex++) {
        const cell = sheet.getCell(rowIndex, columnIndex);
        cell.alignment = alignmentCenter;
        cell.border = borderThinBlack;

        if (rowIndex === headerRowIndex) {
          cell.font = {
            bold: true,
            size: 14,
            color: { argb: "FFFFFFFF" },
          };
          cell.fill = {
            type: "pattern",
            pattern: commonPatternFill,
            fgColor: { argb: colorFillHeader },
          };
        } else if (rowIndex === totalsRowIndex) {
          cell.fill = {
            type: "pattern",
            pattern: commonPatternFill,
            fgColor: { argb: colorFillTotals },
          };
        } else {
          const existingFont = cell.font || {};
          cell.font = {
            ...existingFont,
            size: 12,
          };
        }
      }
    }

    autosizeColumnsWidth(sheet);
    addConditionalFormatting(sheet);

    const excelWidthKoef = 7;
    const excelMaxWidth = 1850;
    let imageWidth = 0;
    sheet.columns.forEach((c) => (imageWidth += c.width));
    imageWidth *= excelWidthKoef;
    if (imageWidth > excelMaxWidth) imageWidth = excelMaxWidth;
    let imageHeight = imageWidth / 4;
    const roundImageWidth = imageWidth - (imageWidth % 4);
    const roundImageHeight = imageHeight - (imageHeight % 4);

    viewerGetScreenShot(roundImageWidth, roundImageHeight, async (blobUrl) => {
      if (blobUrl) {
        try {
          let blob = await fetch(blobUrl).then((r) => r.blob());
          var reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = function () {
            var imageBase64String = reader.result;
            const image = workbook.addImage({
              base64: imageBase64String,
              extension: "jpeg",
            });
            sheet.addImage(image, {
              tl: { col: 0, row: rowCount + 4 },
              ext: { width: roundImageWidth, height: roundImageHeight },
            });
            workbook.xlsx.writeBuffer().then(function (buffer) {
              const blob = new Blob([buffer], { type: FILE_TYPE });
              FileSaver.saveAs(blob, fileName + FILE_EXTENSION);
            });
            logDownloadSchedule();
          };
        } catch (error) {
          // If screenshot fails, still save the Excel file without image
          console.warn('Failed to add screenshot to Excel:', error);
          workbook.xlsx.writeBuffer().then(function (buffer) {
            const blob = new Blob([buffer], { type: FILE_TYPE });
            FileSaver.saveAs(blob, fileName + FILE_EXTENSION);
          });
          logDownloadSchedule();
        }
      } else {
        // If no screenshot available (e.g., in table window), save Excel file without image
        workbook.xlsx.writeBuffer().then(function (buffer) {
          const blob = new Blob([buffer], { type: FILE_TYPE });
          FileSaver.saveAs(blob, fileName + FILE_EXTENSION);
        });
        logDownloadSchedule();
      }
    });
  };

  if (!safeColumns.length) return null;

  return (
    <UniversalButton
      label={isChecked ? t('download_report_selection') : t('download_report')}
      leftIcon={<ReportIcon className="download-excel__button_icon" />}
      size="md"
      isRounded="sm"
      labelColor="white"
      labelHoverColor="white"
      backgroundColor="orange"
      backgroundHoverColor="orange-light"
      onClick={exportToCSV}
    />
  );
};

function addConditionalFormatting(sheet) {
  const cellRefStart = "A2";
  const cellRefEnd = sheet.getCell(sheet.rowCount - 1, sheet.columnCount).address;
  const ref = cellRefStart + ":" + cellRefEnd;
  sheet.addConditionalFormatting({
    ref: ref,
    rules: [
      {
        type: "expression",
        formulae: ["=EXACT($A2, TRUE)"],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFEBF1DE" },
          },
        },
      },
    ],
  });
}

function autosizeColumnsWidth(sheet) {
  sheet.columns.forEach((column) => {
    const lengths = column.values.map((value) => value.toString().length);
    column.width = Math.max(
      Math.max(...lengths.filter((value) => typeof value === "number")) + 2,
      (column.header?.length || 0) + 6
    );
    if (column.width > 100) {
      column.width = 100;
    }
  });
}

function letterFromNumber(number) {
  if (number <= 26) return String.fromCharCode(96 + number).toUpperCase();
  return (
    letterFromNumber(Math.floor(number / 26)) + letterFromNumber(number % 26)
  );
}

export default DownloadInfoTableExcel;
